import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { BillingApiController } from '../../../api-controllers/billing.api-controller';
import { LicenseTypeEnum } from '../../../../../../../../common/src/enums/license-type.enum';
import { DateUtils } from '../../../../../../modules/src/lib/core/utils/date.utils';
import { TranslateService } from '@ngx-translate/core';
import { AdvancedFormControl } from "@magieno/angular-advanced-forms";
import { CreateSubscriptionFormFactory } from './create-subscription-form.factory';
import { AdvancedForm } from "@magieno/angular-advanced-forms";
import {
  CalculateSubscriptionOptions,
} from '../../../../../../../../common/src/options/calculate-subscription.options';
import { FormControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { MoneyUtil } from '../../../../../../../../common/src/utils/money.util';
import { Router } from '@angular/router';
import { v4 as uuid } from 'uuid';
import { BillingCycleEnum } from '../../../../../../../../common/src/enums/billing-cycle.enum';
import { StripeManager } from '../../../managers/stripe.manager';
import { AuthService } from '@auth0/auth0-angular';
import { LoggingService } from '../../../../../../modules/src/lib/core/services/logging.service';
import { take } from 'rxjs/operators';
import {
  MagienoObservabilityManager,
} from '../../../../../../modules/src/lib/core/managers/magieno-observability.manager';

declare global {
  interface Window {
    Stripe: any;
  }
}

@Component({
  selector: 'app-subscription-create',
  templateUrl: './subscription-create.component.html',
  styleUrls: ['./subscription-create.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class SubscriptionCreateComponent implements OnInit, OnDestroy {
  freeTrialEnds: string;

  subtotal: number = 199.00;

  calculateSubscriptionForm: AdvancedForm<CalculateSubscriptionOptions>;

  licenseTypeSubscription: Subscription;

  freeTrialWithoutCreditCard: FormControl<boolean> = new FormControl<boolean>(false);

  paymentCardElement: any;

  stripe: any;

  stripeElements: any;

  billingCycle: BillingCycleEnum = BillingCycleEnum.Monthly;

  isFreeTrialAllowed: boolean = true;

  constructor(private readonly stripeApiController: BillingApiController,
              private readonly dateUtils: DateUtils,
              public readonly translateService: TranslateService,
              private readonly stripeManager: StripeManager,
              public auth: AuthService,
              private readonly router: Router,
              private readonly loggingService: LoggingService,
              private readonly observabilityManager: MagienoObservabilityManager,
              private readonly createSubscriptionFormFactory: CreateSubscriptionFormFactory,
  ) {
    this.calculateSubscriptionForm = this.createSubscriptionFormFactory.createCalculateSubscription();
  }

  getLicenseTypeFormControl(): AdvancedFormControl<LicenseTypeEnum, LicenseTypeEnum, string> {
    return this.calculateSubscriptionForm.formElements.licenseType as AdvancedFormControl<LicenseTypeEnum, LicenseTypeEnum, string>;
  }

  async ngOnInit() {
    try {
      const licenseTypeFormControl = this.getLicenseTypeFormControl();

      this.licenseTypeSubscription = licenseTypeFormControl.value$.subscribe(async value => {
        await this.onLicenseSelect(value.value);
      });

      const licenseType = localStorage.getItem('licenseType');

      if (licenseType === 'questionnaire') {
        await licenseTypeFormControl.setValue(LicenseTypeEnum.Questionnaire);
      } else {
        await licenseTypeFormControl.setValue(LicenseTypeEnum.Individual);
      }


      const idempotencyKey = uuid();

      const response = await this.stripeApiController.setupSubscription(idempotencyKey);


      this.loggingService.info('Received response from Stripe', { response });

      const options = {
        clientSecret: response.client_secret,
        // Fully customizable with appearance API.
        appearance: {},
      };

      this.auth.user$.pipe(take(1)).subscribe(user => {
        // Set up Stripe.js and Elements to use in checkout form, passing the client secret obtained in step 3
        this.stripe = window.Stripe(this.stripeManager.getPublishableKey(user.email), { locale: this.translateService.currentLang });
        this.stripeElements = this.stripe.elements(options);

        // Create and mount the Payment Element
        this.paymentCardElement = this.stripeElements.create('payment');
        this.paymentCardElement.mount('#payment-element');
      });
    } catch (error) {
      this.loggingService.error('Error initializing subscription create component', error);
      await this.observabilityManager.sendAlert('CRITICAL', 'subscription-create - Error initializing subscription create component.', {
        error,
      });
    }

  }

  async ngOnDestroy() {
    this.licenseTypeSubscription.unsubscribe();
  }

  async onLicenseSelect(licenseType: LicenseTypeEnum) {
    this.billingCycle = undefined;
    const response = await this.stripeApiController.calculateSubscription({ licenseType: licenseType });
    this.billingCycle = response.billingCycle;
    this.isFreeTrialAllowed = response.isFreeTrialAllowed;
    if (this.isFreeTrialAllowed) {
      this.freeTrialEnds = this.dateUtils.getText(response.freeTrialEndsAt);
    }
    this.subtotal = MoneyUtil.centsToDollars(response.subtotal);
  }

  async submit() {
    try {
      this.loggingService.info('Submitting Stripe form.');

      const { error } = await this.stripe.confirmSetup({
        //`Elements` instance that was used to create the Payment Element
        elements: this.stripeElements,
        confirmParams: {
          return_url: window.location.protocol + '//' + window.location.hostname + (window.location.port ? (':' + window.location.port) : '') + '/onboarding/subscription/created?licenseType=' + this.getLicenseTypeFormControl().value + '&setupFreeTrial=' + true + '&setupFreeTrialWithoutCreditCard=' + false,
        },
      });

      if (error) {
        this.loggingService.error(error);
        // // We still try to validate the credit card, but if there is an error, we simply ignore it.
        // if (this.freeTrialWithoutCreditCard.value && this.stripeElements) {
        //   this.loggingService.info('Navigating away from the page since there \'free trial without credit card\' option was selected.');
        //   const result = await this.router.navigateByUrl('/onboarding/subscription/created?licenseType=' + this.getLicenseTypeFormControl().value + '&setupFreeTrial=' + true + '&setupFreeTrialWithoutCreditCard=' + true);
        //   this.loggingService.info('navigateByUrl result.', { result });
        //   return;
        // }

        // This point will only be reached if there is an immediate error when
        // confirming the payment. Show error to your customer (for example, payment
        // details incomplete)
        const messageContainer = document.querySelector('#error-message');
        messageContainer.textContent = error.message;
      }
    } catch (error) {
      this.auth.user$.pipe(take(1)).subscribe(async (user) => {
        await this.observabilityManager.sendAlert('CRITICAL', 'subscription-create - Error submitting the setup to Stripe.', {
          error,
          user,

        });
      });

      this.loggingService.error('Error submitting the setup to Stripe.', error);
    }

  }
}
